import { useEffect, useMemo, useState } from 'react';

type Listener = (terms: string[]) => void;

const termUsage = new Map<string, number>();
const listeners = new Set<Listener>();

const normalizeTerms = (terms: string[]) =>
  terms.map(term => term.trim()).filter((term): term is string => Boolean(term));

const notify = () => {
  const snapshot = Array.from(termUsage.keys());

  listeners.forEach(listener => listener(snapshot));
};

export const registerProtectedTerms = (terms: string[]) => {
  const normalized = normalizeTerms(terms);

  if (!normalized.length) {
    // eslint-disable-next-line @typescript-eslint/no-empty-function -- we need to return a function
    return () => {};
  }

  normalized.forEach(term => {
    termUsage.set(term, (termUsage.get(term) ?? 0) + 1);
  });

  notify();

  return () => {
    normalized.forEach(term => {
      const count = termUsage.get(term);

      if (!count) {
        return;
      }

      if (count <= 1) {
        termUsage.delete(term);
      } else {
        termUsage.set(term, count - 1);
      }
    });

    notify();
  };
};

export const useProtectedTermsSubscription = () => {
  const [terms, setTerms] = useState<string[]>(() => Array.from(termUsage.keys()));

  useEffect(() => {
    const listener: Listener = snapshot => setTerms(snapshot);

    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  }, []);

  return terms;
};

export const useRegisterTranslationProtectedTerms = (terms: string[]) => {
  const normalizedTerms = useMemo(() => normalizeTerms(terms), [terms]);
  const termsKey = normalizedTerms.join('|');

  useEffect(() => {
    if (!normalizedTerms.length) {
      return;
    }

    const unregister = registerProtectedTerms(normalizedTerms);

    return unregister;
  }, [normalizedTerms, termsKey]);
};
